import { AxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';

import { useProcessApiClient } from '@/api/clients/useProcessApiClient.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

export type PdfOfferScope = 'all' | 'current';
export type PdfPreviewType = 'offer' | 'offerSelected' | 'offerSelection' | 'realization';

export const OFFER_PRINT_TEMPLATE_MISSING = 'OFFER_PRINT_TEMPLATE_MISSING';

export type PdfPreviewError = {
  code?: string;
  message?: string;
};

type Params = {
  multiProcessOfferGroupId: string;
  processId: string;
  processOfferId: string;
};

type Return = {
  error: PdfPreviewError | null;
  isLoading: boolean;
  loadPdf: (type: PdfPreviewType, scope?: PdfOfferScope) => Promise<void>;
  pdfUrl: string | null;
};

const readErrorBody = async (error: unknown): Promise<PdfPreviewError> => {
  const data = (error as AxiosError)?.response?.data;
  const raw =
    data instanceof Blob ? await data.text().catch(() => '') : data ? JSON.stringify(data) : '';
  try {
    const parsed = JSON.parse(raw) as PdfPreviewError;
    return { code: parsed.code, message: parsed.message };
  } catch {
    return { message: (error as Error)?.message };
  }
};

export const useProcessOfferPdfPreview = (params: Params): Return => {
  const { processOfferFileGenerator } = useProcessApiClient();
  const { userId } = useUserContext();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<PdfPreviewError | null>(null);
  const previousUrlRef = useRef<string | null>(null);

  const loadPdf = useCallback(
    async (type: PdfPreviewType, scope: PdfOfferScope = 'current') => {
      setIsLoading(true);
      setError(null);
      if (previousUrlRef.current) {
        URL.revokeObjectURL(previousUrlRef.current);
        previousUrlRef.current = null;
      }
      setPdfUrl(null);

      const isOfferType = type !== 'realization';
      const printMode =
        type === 'offerSelection' ? 'SELECTION' : type === 'offerSelected' ? 'SELECTED' : undefined;

      try {
        const response = isOfferType
          ? await processOfferFileGenerator.generateOfferFile(
              userId,
              params.processId,
              params.multiProcessOfferGroupId,
              scope === 'current' ? params.processOfferId : undefined,
              printMode,
              { responseType: 'blob' },
            )
          : await processOfferFileGenerator.generateRealizationOfferFile(
              userId,
              params.processId,
              params.processOfferId,
              { responseType: 'blob' },
            );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        previousUrlRef.current = url;
        setPdfUrl(url);
      } catch (caught) {
        setError(await readErrorBody(caught));
      } finally {
        setIsLoading(false);
      }
    },
    [userId, params.processId, params.multiProcessOfferGroupId, params.processOfferId],
  );

  return { error, isLoading, loadPdf, pdfUrl };
};
